/**
 * SA-1032 (spec D4): the words of a task page whose task is no longer live —
 * what ended it, then where the process is NOW (owner ruling, 8 Oct), read
 * from the record read. Pure. Times go through `stepLiveText.whenText`
 * (hand-formatted, local time, `now` injectable).
 */
import type { RecordProcessRead, TaskRead } from '@/types/process'
import { exitWord } from './stepDrawerText'
import { lowerWords, outcomeWords, whenText } from './stepLiveText'

type RecordRead = RecordProcessRead | null | undefined

/** The task page shows its form only while a person can still work the task. */
export const LIVE_TASK_STATUSES: ReadonlySet<string> = new Set(['open', 'claimed'])

/** A wait's event, when the server names it as the step's only possible end. */
const EVENT_PHRASE: Readonly<Record<string, string>> = {
  feasibility_recorded: 'a feasibility result was recorded',
  quote_version_sent: 'a quote was sent',
  quote_response_recorded: 'the customer’s response was recorded',
}

const REASON_PHRASE: Readonly<Record<string, string>> = {
  superseded: 'a follow-up task replaced this one',
  revision: 'the intake was revised and the process went back',
  reopened: 'an earlier step was re-opened',
}

/** " 8 Oct 14:02", or nothing when the time is unknown. */
function at(iso: string | null | undefined, now: Date): string {
  const when = whenText(iso, now)
  return when ? ` ${when}` : ''
}

function exitLabel(task: TaskRead): string | null {
  if (!task.result_exit) return null
  return task.exits.find((e) => e.name === task.result_exit)?.label ?? exitWord(task.result_exit)
}

/** Why a cancelled task ended, from `ended.reason`; null when unknown. */
function endedWhy(task: TaskRead, record: RecordRead): string | null {
  const ended = task.ended
  if (!ended) return null
  switch (ended.reason) {
    case 'completed':
      return ended.event ? EVENT_PHRASE[ended.event] ?? lowerWords(ended.event) : 'the step was completed'
    case 'closed': {
      const outcome = record?.status === 'closed' ? outcomeWords(record.closed_outcome) : null
      return outcome ? `the record was closed (${outcome})` : 'the record was closed'
    }
    case 'superseded':
    case 'revision':
    case 'reopened':
      return REASON_PHRASE[ended.reason]
    default:
      return null
  }
}

/** What happened to the task; null while a person can still work it. */
export function firstSentence(task: TaskRead, record: RecordRead, now: Date = new Date()): string | null {
  const ai = task.kind === 'ai'
  switch (task.status) {
    case 'open':
    case 'claimed':
      return null
    case 'done': {
      if (task.kind === 'automation') return `The automation completed${at(task.completed_at, now)}.`
      if (ai) return `The AI answered${at(task.completed_at, now)}.`
      const name = task.result?.completed_by_name
      const exit = exitLabel(task)
      return `Completed${at(task.completed_at, now)}${name ? ` by ${name}` : ''}${exit ? ` with “${exit}”` : ''}.`
    }
    case 'failed':
      return ai ? `The AI call failed${at(task.completed_at, now)}.` : `The automation failed${at(task.completed_at, now)}.`
    case 'pending':
      return ai ? 'Queued for the AI.' : 'Queued for the automation.'
    case 'running':
      return ai ? 'The AI is working on this step.' : 'The automation is running.'
    case 'cancelled': {
      const why = endedWhy(task, record)
      return `Ended${at(task.ended?.at, now)}${why ? `: ${why}` : ''}.`
    }
    default:
      // A status this page does not know still says something, never a form.
      return `This task is ${lowerWords(task.status)}.`
  }
}

/** Where the process is now; null without a usable record read. */
export function nowSentence(task: TaskRead, record: RecordRead): string | null {
  if (!record) return null
  if (record.status === 'closed') {
    // The first sentence already says so, with the outcome.
    if (task.ended?.reason === 'closed') return null
    const outcome = outcomeWords(record.closed_outcome)
    return outcome ? `The record is closed as ${outcome}.` : 'The record is closed.'
  }
  const current = record.status === 'running' ? record.current_step : null
  if (!current) return null
  const waiting = current.seat_label
    ? `, waiting on ${current.seat_label}`
    : current.waiting_for_event ? `, waiting for ${lowerWords(current.waiting_for_event)}` : ''
  return `The process is now at ${current.number} ${current.title}${waiting}.`
}

/** The task page's status line: both sentences; null for a live task. */
export function taskStatusLine(task: TaskRead, record: RecordRead, now: Date = new Date()): string | null {
  const first = firstSentence(task, record, now)
  if (first === null) return null
  const then = nowSentence(task, record)
  return then ? `${first} ${then}` : first
}
